import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  ShieldCheck,
  Award,
  Lock,
  Clock,
  Scale,
  Sparkles,
  CalendarCheck,
  MessageCircle,
  Heart,
  Briefcase,
  Building2,
  FileText,
  Gavel,
  Users,
  Home as HomeIcon,
  Lightbulb,
  Shield,
  Check,
} from "lucide-react";
import { useI18n } from "../lib/i18n";
import { Button } from "../components/ui/button";
import SEO, { orgJsonLd, faqJsonLd } from "../components/SEO";
import { SERVICES } from "../data/services";
import { buildWhatsAppLink, getPageWaLink, CONSULTATION_FEE_KWD } from "../data/firm";

const ICONS = { Heart, Briefcase, Building2, Scale, Shield, FileText, Gavel, Users, Home: HomeIcon, Lightbulb };

const HOME_FAQS_AR = [
  { q: "كم تكلفة الاستشارة القانونية؟", a: "تبلغ تكلفة الاستشارة الأولية 50 ديناراً كويتياً وتتم في المكتب أو هاتفياً أو عبر Zoom حسب اختيارك." },
  { q: "هل بياناتي ومستنداتي سرية؟", a: "نعم تماماً. كل ما تشاركه معنا محمي بسرية العلاقة بين المحامي وموكله، ولا يُفصح عنه لأي طرف خارج المكتب." },
  { q: "كم يستغرق الرد على الاستفسار؟", a: "نرد على كل استفسار خلال يوم عمل واحد كحد أقصى، وعادةً خلال ساعتين في أوقات الدوام." },
  { q: "هل يمكنني التشاور بالذكاء الاصطناعي قبل الحجز؟", a: "نعم، استخدم \"الذكاء القانوني\" لطرح سؤالك وفهم موقفك الأولي قبل تحديد موعد." },
];
const HOME_FAQS_EN = [
  { q: "Consultation fee?", a: "KWD 50 for the initial consultation, in person, by phone, or via Zoom." },
  { q: "Are my documents confidential?", a: "Strictly. Attorney-client privilege applies; nothing leaves the firm." },
  { q: "Response time?", a: "Within one business day at the latest — usually within two hours during working hours." },
  { q: "Can I try the AI assistant first?", a: "Yes — use AI Legal Assistant to scope your matter before booking." },
];

const TrustBadge = ({ icon: Icon, label }) => (
  <div className="flex items-center gap-2 text-xs sm:text-sm" style={{ color: "var(--ink-soft)" }}>
    <Icon className="w-4 h-4" style={{ color: "var(--gold-deep)" }} />
    <span>{label}</span>
  </div>
);

export default function Home() {
  const { t, lang } = useI18n();
  const isAr = lang === "ar";
  const waHome = getPageWaLink("home", lang);

  return (
    <div data-testid="home-page">
      <SEO
        path="/"
        titleAr="مجموعة الدخيل القانونية | استشارات وتمثيل قانوني في دولة الكويت"
        titleEn="Aldakheel Legal Group | Legal Consultation & Representation in Kuwait"
        descriptionAr="منصة قانونية حديثة تجمع بين خبرة المحاماة والتواصل المباشر عبر واتساب والذكاء القانوني. أحوال شخصية، تجاري، عمالي، جزائي، شركات. تواصل معنا اليوم."
        descriptionEn="A modern Kuwait legal platform combining experienced advocacy, easy booking, and an AI legal assistant. Family, commercial, labor, criminal, corporate law. Book today."
        jsonLd={[orgJsonLd(), faqJsonLd(isAr ? HOME_FAQS_AR : HOME_FAQS_EN)]}
      />

      {/* HERO ---------------------------------------------------------- */}
      <section className="relative overflow-hidden" style={{ background: "var(--bg)" }}>
        <div className="absolute inset-0 -z-10">
          <div
            className="absolute inset-0"
            style={{
              background:
                "radial-gradient(ellipse at top, rgba(201,169,97,0.12) 0%, rgba(255,255,255,0) 55%)",
            }}
          />
          <div
            className="absolute -top-32 -end-32 w-[480px] h-[480px] rounded-full blur-3xl opacity-30"
            style={{ background: "var(--gold)" }}
          />
        </div>
        <div className="container-luxe pt-16 lg:pt-24 pb-16 lg:pb-24">
          <div className="rise flex flex-col items-center">
            <p className="eyebrow mb-3">
              {isAr ? "مجموعة الدخيل القانونية · الكويت" : "Aldakheel Legal Group · Kuwait"}
            </p>
            <img
              src="/branding/logo-transparent.png"
              alt="Aldakheel Legal Group"
              className="hero-logo h-40 sm:h-52 lg:h-72 xl:h-80 w-auto object-contain mb-8"
            />
          </div>

          {isAr ? (
            <>
              <h1 className="rise rise-2 mt-6 font-arabic text-center mx-auto max-w-4xl"
                  style={{ fontSize: "clamp(2.25rem, 5.5vw, 4rem)", lineHeight: 1.2, fontWeight: 800, color: "var(--heading)" }}>
                مجموعة الدخيل القانونية
              </h1>
              <p className="rise rise-3 mt-6 max-w-2xl mx-auto text-center font-arabic text-lg sm:text-xl leading-relaxed"
                 style={{ color: "var(--ink-soft)" }}>
                منصة قانونية كويتية تجمع بين الخبرة القانونية والذكاء الرقمي.
              </p>
            </>
          ) : (
            <>
              <h1 className="rise rise-2 mt-6 font-display text-center mx-auto max-w-4xl"
                  style={{ fontSize: "clamp(2.5rem, 6vw, 4.5rem)", lineHeight: 1.1, color: "var(--heading)" }}>
                Aldakheel Legal Group
              </h1>
              <p className="rise rise-3 mt-6 max-w-2xl mx-auto text-center text-lg leading-relaxed"
                 style={{ color: "var(--ink-soft)" }}>
                A Kuwaiti legal platform combining seasoned advocacy with digital intelligence.
              </p>
            </>
          )}

          <div className="rise rise-4 mt-12 flex flex-wrap items-center justify-center gap-3">
            <a href={waHome} target="_blank" rel="noopener noreferrer" data-testid="hero-cta-book">
              <Button className="btn-gold rounded-sm px-7 h-12 text-[15px]">
                <CalendarCheck className="w-4 h-4 me-2" />
                {isAr ? "احجز استشارة عبر واتساب" : "Book via WhatsApp"}
              </Button>
            </a>
            <Link to="/ai-legal-assistant" data-testid="hero-cta-ai">
              <Button className="btn-navy rounded-sm px-7 h-12 text-[15px]">
                <Sparkles className="w-4 h-4 me-2" />
                {isAr ? "اسأل الذكاء القانوني" : "Ask the AI"}
              </Button>
            </Link>
            <a href={waHome} target="_blank" rel="noopener noreferrer" data-testid="hero-cta-whatsapp">
              <Button className="btn-whatsapp rounded-sm px-7 h-12 text-[15px]">
                <MessageCircle className="w-4 h-4 me-2" />
                {isAr ? "تواصل واتساب" : "WhatsApp"}
              </Button>
            </a>
          </div>

          {/* Trust badges row — 4 succinct items per the new spec */}
          <div className="rise rise-4 mt-12 flex flex-wrap items-center justify-center gap-x-8 gap-y-3">
            <TrustBadge icon={Award} label={isAr ? "محامٍ مرخص في الكويت" : "Kuwait-licensed"} />
            <TrustBadge icon={Lock} label={isAr ? "سرية تامة" : "Strict confidentiality"} />
            <TrustBadge icon={Clock} label={isAr ? "استشارة خلال وقت قصير" : "Quick scheduling"} />
            <TrustBadge icon={Sparkles} label={isAr ? "ذكاء قانوني مساعد" : "AI legal assistant"} />
          </div>
        </div>
      </section>

      {/* OFFICE INTRO -------------------------------------------------- */}
      <section className="section-y" style={{ background: "var(--paper)" }}>
        <div className="container-luxe grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <p className="eyebrow">{isAr ? "عن المكتب" : "About the firm"}</p>
            <h2 className={`mt-3 ${isAr ? "font-arabic" : "font-display"} gold-rule`}
                style={{ fontSize: "clamp(1.75rem, 3.5vw, 2.5rem)", fontWeight: isAr ? 800 : 500, color: "var(--heading)" }}>
              {isAr ? "مكتب كويتي يصنع الفرق في القضية" : "A Kuwaiti firm that shapes outcomes"}
            </h2>
            <p className="mt-8 text-lg leading-relaxed" style={{ color: "var(--ink-soft)" }}>
              {isAr
                ? "نحضر عن عملائنا أمام درجات القضاء الكويتي كافة — أول درجة، الاستئناف، التمييز — ونقدّم المشورة للمؤسسين والعائلات والشركات في القضايا التي تصوغ مستقبلهم. خبرة 9+ سنوات، 2000+ قضية، 6+ دول."
                : "We appear before every degree of the Kuwaiti judiciary — first instance, appeal, Cassation — and counsel founders, families, and businesses on the matters that shape their future. 9+ years, 2,000+ matters, 6+ countries advised."}
            </p>
            <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-2xl">
              {[
                ["9+", isAr ? "سنوات خبرة" : "Years"],
                ["2000+", isAr ? "قضية" : "Cases"],
                ["10", isAr ? "مجالات" : "Practices"],
                ["6+", isAr ? "دول" : "Countries"],
              ].map(([v, l]) => (
                <div key={v}>
                  <div className="font-display text-3xl lg:text-4xl" style={{ color: "var(--gold-deep)" }}>{v}</div>
                  <div className="mt-1 text-sm" style={{ color: "var(--ink-soft)" }}>{l}</div>
                </div>
              ))}
            </div>
            <Link to="/about" className="inline-flex items-center gap-2 mt-10 text-sm font-medium"
                  style={{ color: "var(--heading)" }}>
              {isAr ? "اقرأ قصتنا" : "Read our story"}
              {isAr ? <ArrowLeft className="w-4 h-4" /> : <ArrowRight className="w-4 h-4" />}
            </Link>
          </div>
          <div className="lg:col-span-5">
            <div className="relative aspect-[4/5] overflow-hidden rounded-sm" style={{ background: "var(--beige)" }}>
              <img
                src="https://customer-assets.emergentagent.com/job_legal-group-site/artifacts/p60tyiuq_ChatGPT%20Image%20May%2023%2C%202026%2C%2002_08_49%20AM.png"
                alt={isAr ? "مكتب مجموعة الدخيل القانونية" : "Aldakheel Legal Group office"}
                loading="lazy"
                className="w-full h-full object-cover"
              />
              <div className="absolute bottom-0 inset-x-0 h-1/3"
                   style={{ background: "linear-gradient(180deg, transparent 0%, rgba(11,27,51,0.55) 100%)" }} />
              <div className="absolute bottom-6 start-6 end-6 text-white">
                <p className="text-xs uppercase tracking-[0.2em] opacity-90 mb-1">{isAr ? "المقر" : "Office"}</p>
                <p className={`${isAr ? "font-arabic" : "font-display"} text-lg`}>
                  {isAr ? "مدينة الكويت — مبنى الفتوح" : "Kuwait City — Al-Futooh Building"}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PRACTICE AREAS ------------------------------------------------ */}
      <section className="section-y" style={{ background: "var(--bg)" }}>
        <div className="container-luxe">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-12">
            <div>
              <p className="eyebrow">{isAr ? "مجالات الممارسة" : "Practice areas"}</p>
              <h2 className={`mt-3 ${isAr ? "font-arabic" : "font-display"} gold-rule`}
                  style={{ fontSize: "clamp(1.75rem, 3.5vw, 2.5rem)", fontWeight: isAr ? 800 : 500, color: "var(--heading)" }}>
                {isAr ? "عشرة تخصصات تحت سقف واحد" : "Ten disciplines under one roof"}
              </h2>
            </div>
            <Link to="/services" className="inline-flex items-center gap-2 text-sm font-medium"
                  style={{ color: "var(--gold-deep)" }}>
              {isAr ? "كل خدماتنا" : "All services"}
              {isAr ? <ArrowLeft className="w-4 h-4" /> : <ArrowRight className="w-4 h-4" />}
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {SERVICES.map((s) => {
              const Icon = ICONS[s.icon] || Scale;
              return (
                <Link
                  key={s.slug}
                  to={`/services/${s.slug}`}
                  data-testid={`home-service-${s.slug}`}
                  className="luxe-card p-6 group"
                >
                  <div className="flex items-start justify-between">
                    <div className="w-12 h-12 rounded-sm flex items-center justify-center"
                         style={{ background: "var(--beige)", color: "var(--gold-deep)" }}>
                      <Icon className="w-6 h-6" />
                    </div>
                    {isAr ? (
                      <ArrowLeft className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: "var(--gold-deep)" }} />
                    ) : (
                      <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: "var(--gold-deep)" }} />
                    )}
                  </div>
                  <h3 className={`mt-5 ${isAr ? "font-arabic" : "font-display"} text-xl`}
                      style={{ fontWeight: isAr ? 700 : 500, color: "var(--heading)" }}>
                    {isAr ? s.titleAr : s.titleEn}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed" style={{ color: "var(--ink-soft)" }}>
                    {isAr ? s.teaserAr : s.teaserEn}
                  </p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* AI ASSISTANT PREVIEW ----------------------------------------- */}
      <section className="section-y relative overflow-hidden" style={{ background: "var(--navy)", color: "#fff" }}>
        <div className="absolute -bottom-40 -start-40 w-[520px] h-[520px] rounded-full blur-3xl opacity-25"
             style={{ background: "var(--gold)" }} />
        <div className="container-luxe relative grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <p className="eyebrow" style={{ color: "var(--gold-soft)" }}>
              {isAr ? "الذكاء القانوني" : "AI Legal Assistant"}
            </p>
            <h2 className={`mt-3 ${isAr ? "font-arabic" : "font-display"}`}
                style={{ fontSize: "clamp(1.75rem, 3.5vw, 2.75rem)", fontWeight: isAr ? 800 : 500 }}>
              {isAr ? "اسأل سامي. افهم موقفك. خذ القرار." : "Ask Sami. Understand your position. Decide."}
            </h2>
            <p className="mt-6 text-lg leading-relaxed" style={{ color: "rgba(245,241,232,0.78)" }}>
              {isAr
                ? "ارفع مستندك، اشرح حالتك، واحصل على تحليل فوري: المشكلة، المستندات الناقصة، المستوى التقديري للمخاطر، والخطوات التالية الموصى بها — مدعومة بمكتبة قوانين الكويت كاملةً."
                : "Upload your document, describe your matter, and get an instant analysis: issue spotting, missing evidence, risk level, and next steps — powered by the complete Kuwait legal library."}
            </p>
            <div className="mt-6 grid sm:grid-cols-2 gap-3 text-sm">
              {[
                isAr ? "تلخيص المستندات" : "Document summary",
                isAr ? "تحديد المشاكل القانونية" : "Issue spotting",
                isAr ? "المستندات الناقصة" : "Missing documents",
                isAr ? "الخطوات التالية الموصى بها" : "Recommended next steps",
              ].map((f) => (
                <div key={f} className="flex items-center gap-2">
                  <Check className="w-4 h-4 shrink-0" style={{ color: "var(--gold-soft)" }} />
                  <span style={{ color: "rgba(245,241,232,0.85)" }}>{f}</span>
                </div>
              ))}
            </div>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/ai-legal-assistant">
                <Button className="btn-gold rounded-sm px-7 h-12">
                  {isAr ? "جرّب الذكاء القانوني" : "Try the AI Assistant"}
                </Button>
              </Link>
              <a href={waHome} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="rounded-sm px-7 h-12 border-white/30 text-white hover:bg-white/10 hover:text-white">
                  {isAr ? "أو احجز موعداً عبر واتساب" : "Or book via WhatsApp"}
                </Button>
              </a>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="rounded-sm p-6 backdrop-blur" style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(201,169,97,0.3)" }}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 rounded-full flex items-center justify-center"
                     style={{ background: "var(--gold)", color: "var(--navy-deep)" }}>
                  <Sparkles className="w-4 h-4" />
                </div>
                <div>
                  <p className="text-sm font-medium">Sami</p>
                  <p className="text-xs opacity-60">{isAr ? "المستشار الذكي" : "AI Legal Assistant"}</p>
                </div>
              </div>
              <p className={`${isAr ? "font-arabic" : ""} text-sm leading-relaxed mb-4`} style={{ color: "rgba(245,241,232,0.78)" }}>
                {isAr
                  ? "وفقاً للمادة 51 من قانون العمل الكويتي رقم 6 لسنة 2010، تُحتسب مكافأة نهاية الخدمة بواقع 15 يوماً عن كل سنة من السنوات الخمس الأولى، ثم شهر كامل عن كل سنة بعد ذلك..."
                  : "Under Article 51 of Kuwait Labor Law 6/2010, end-of-service indemnity is 15 days for each of the first 5 years, then one full month per year thereafter..."}
              </p>
              <div className="text-[11px] uppercase tracking-wider mt-3" style={{ color: "var(--gold-soft)" }}>
                {isAr ? "← مصدر: قانون العمل المادة 51" : "← Source: Labor Law, Art. 51"}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BOOKING PREVIEW ---------------------------------------------- */}
      <section className="section-y" style={{ background: "var(--paper)" }}>
        <div className="container-luxe grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <div className="luxe-card p-8" style={{ background: "var(--bg)" }}>
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-sm flex items-center justify-center"
                       style={{ background: "var(--beige)", color: "var(--gold-deep)" }}>
                    <CalendarCheck className="w-5 h-5" />
                  </div>
                  <div>
                    <p className={`${isAr ? "font-arabic" : ""} font-medium`} style={{ color: "var(--heading)" }}>
                      {isAr ? "استشارة قانونية أولية" : "Initial legal consultation"}
                    </p>
                    <p className="text-xs" style={{ color: "var(--ink-soft)" }}>
                      {isAr ? "30 دقيقة · سرية تامة" : "30 minutes · strictly confidential"}
                    </p>
                  </div>
                </div>
                <div className="text-end">
                  <p className="text-2xl font-display" style={{ color: "var(--gold-deep)" }}>{CONSULTATION_FEE_KWD}</p>
                  <p className="text-xs" style={{ color: "var(--ink-soft)" }}>{isAr ? "د.ك" : "KWD"}</p>
                </div>
              </div>
              <ul className="space-y-3 text-sm" style={{ color: "var(--ink)" }}>
                {[
                  isAr ? "أحوال شخصية، عمالي، تجاري، مدني، جزائي، شركات، عقود، عقارات، ملكية فكرية" : "Family, labor, commercial, civil, criminal, corporate, contracts, real estate, IP",
                  isAr ? "اختر طريقة الاستشارة: مكتب / اتصال / Zoom / واتساب" : "In office, phone, Zoom, or WhatsApp",
                  isAr ? "رفع مستندات اختياري (PDF / JPG / PNG / DOCX)" : "Optional document upload",
                  isAr ? "تأكيد الموعد عبر واتساب" : "Appointment confirmed on WhatsApp",
                ].map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "var(--gold-deep)" }} />
                    <span className={isAr ? "font-arabic" : ""}>{f}</span>
                  </li>
                ))}
              </ul>
              <a href={waHome} target="_blank" rel="noopener noreferrer" className="block mt-8">
                <Button className="btn-gold rounded-sm w-full h-12">
                  {isAr ? "تواصل عبر واتساب" : "Contact via WhatsApp"}
                </Button>
              </a>
            </div>
          </div>
          <div className="lg:col-span-6 order-1 lg:order-2">
            <p className="eyebrow">{isAr ? "حجز الاستشارة" : "Book a consultation"}</p>
            <h2 className={`mt-3 ${isAr ? "font-arabic" : "font-display"} gold-rule`}
                style={{ fontSize: "clamp(1.75rem, 3.5vw, 2.5rem)", fontWeight: isAr ? 800 : 500, color: "var(--heading)" }}>
              {isAr ? "ثلاث خطوات للحصول على إجابتك القانونية" : "Three steps to your legal answer"}
            </h2>
            <ol className="mt-8 space-y-6">
              {[
                [isAr ? "اختر نوع الاستشارة" : "Choose your matter", isAr ? "حدد المجال القانوني وطريقة التواصل" : "Pick the area & contact method"],
                [isAr ? "املأ بياناتك" : "Share your details", isAr ? "اسم، هاتف، ملخص قصير، مستندات اختيارية" : "Name, phone, short brief, optional files"],
                [isAr ? "أكّد الموعد" : "Confirm slot", isAr ? "نؤكد موعدك عبر واتساب، وتُدفع الرسوم (50 د.ك) قبل الجلسة" : "We confirm your slot on WhatsApp; the KWD 50 fee is paid before the session"],
              ].map(([title, body], i) => (
                <li key={title} className="flex gap-4">
                  <span className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 text-sm font-medium"
                        style={{ background: "var(--navy)", color: "var(--gold-soft)" }}>{i + 1}</span>
                  <div>
                    <p className={`${isAr ? "font-arabic" : ""} font-medium`} style={{ color: "var(--heading)" }}>{title}</p>
                    <p className={`${isAr ? "font-arabic" : ""} mt-1 text-sm`} style={{ color: "var(--ink-soft)" }}>{body}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US ------------------------------------------------- */}
      <section className="section-y" style={{ background: "var(--bg)" }}>
        <div className="container-luxe">
          <div className="text-center max-w-3xl mx-auto">
            <p className="eyebrow">{isAr ? "لماذا نحن" : "Why us"}</p>
            <h2 className={`mt-3 ${isAr ? "font-arabic" : "font-display"} gold-rule center`}
                style={{ fontSize: "clamp(1.75rem, 3.5vw, 2.5rem)", fontWeight: isAr ? 800 : 500, color: "var(--heading)" }}>
              {isAr ? "خمسة أسباب تجعلك تختار مجموعة الدخيل" : "Five reasons clients choose Aldakheel"}
            </h2>
          </div>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {[
              [Sparkles, isAr ? "وضوح قانوني" : "Legal clarity", isAr ? "كل قضية تُبنى على نص قانوني واجتهاد التمييز المستقر." : "Each matter anchored in statute and settled jurisprudence."],
              [Lock, isAr ? "سرية كاملة" : "Total confidentiality", isAr ? "كل ما تشاركه محمي بسرية العلاقة بين المحامي وموكله." : "Everything you share is protected by attorney-client privilege."],
              [Award, isAr ? "متابعة رقمية" : "Digital follow-up", isAr ? "متابعة عبر واتساب، ومساعد قانوني ذكي على مدار الساعة." : "Follow-up on WhatsApp and a 24/7 AI legal assistant."],
              [Scale, isAr ? "حلول عملية" : "Practical outcomes", isAr ? "قرارات تستطيع تنفيذها فوراً، بلا نظريات." : "Decisions you can act on immediately — no theorising."],
              [Clock, isAr ? "سرعة في التواصل" : "Fast response", isAr ? "رد على كل استفسار خلال يوم عمل واحد." : "Every enquiry answered within one business day."],
            ].map(([Icon, title, body]) => (
              <div key={title} className="text-center">
                <div className="mx-auto w-14 h-14 rounded-full flex items-center justify-center"
                     style={{ background: "var(--beige)", color: "var(--gold-deep)" }}>
                  <Icon className="w-6 h-6" />
                </div>
                <h3 className={`mt-5 ${isAr ? "font-arabic" : "font-display"} text-lg`}
                    style={{ fontWeight: isAr ? 700 : 500, color: "var(--heading)" }}>{title}</h3>
                <p className="mt-3 text-sm leading-relaxed" style={{ color: "var(--ink-soft)" }}>{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CASE RESULTS -------------------------------------------------- */}
      <section className="section-y" style={{ background: "var(--beige)" }}>
        <div className="container-luxe">
          <div className="text-center max-w-3xl mx-auto">
            <p className="eyebrow">{isAr ? "نتائج مختارة" : "Selected results"}</p>
            <h2 className={`mt-3 ${isAr ? "font-arabic" : "font-display"} gold-rule center`}
                style={{ fontSize: "clamp(1.75rem, 3.5vw, 2.5rem)", fontWeight: isAr ? 800 : 500, color: "var(--heading)" }}>
              {isAr ? "إنجازات قانونية حقيقية، بأسماء مجهّلة" : "Real legal wins, anonymized"}
            </h2>
          </div>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { slug: "custody-dismissed", ar: "رفض دعوى إسقاط حضانة", en: "Custody removal claim dismissed" },
              { slug: "contract-rescinded", ar: "الحكم بفسخ عقد ورد المبالغ", en: "Contract rescinded; funds returned" },
              { slug: "criminal-acquittal", ar: "البراءة في قضية جزائية", en: "Acquittal in a criminal matter" },
              { slug: "claim-defeated", ar: "رفض مطالبات مالية غير مستحقة", en: "Unjust monetary claim defeated" },
            ].map((c, i) => (
              <div key={c.slug} className="luxe-card p-6" style={{ background: "var(--bg)" }}>
                <p className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gold-deep)" }}>
                  {isAr ? `قضية ${i + 1}` : `Case ${i + 1}`}
                </p>
                <p className={`mt-4 ${isAr ? "font-arabic" : "font-display"} text-lg leading-snug`}
                   style={{ fontWeight: isAr ? 700 : 500, color: "var(--heading)" }}>
                  {isAr ? c.ar : c.en}
                </p>
              </div>
            ))}
          </div>
          <p className="mt-12 max-w-3xl mx-auto text-xs text-center leading-relaxed" style={{ color: "var(--ink-soft)" }}>
            {isAr
              ? "النتائج السابقة لا تعني ضمان تحقيق النتيجة ذاتها في قضايا أخرى، إذ تخضع كل قضية لوقائعها ومستنداتها وتقدير المحكمة."
              : "Prior results do not guarantee a similar outcome — every matter depends on its facts, documents, and court discretion."}
          </p>
        </div>
      </section>

      {/* FAQ ------------------------------------------------------------ */}
      <section className="section-y" style={{ background: "var(--bg)" }}>
        <div className="container-luxe max-w-4xl">
          <div className="text-center mb-12">
            <p className="eyebrow">{isAr ? "أسئلة شائعة" : "FAQ"}</p>
            <h2 className={`mt-3 ${isAr ? "font-arabic" : "font-display"} gold-rule center`}
                style={{ fontSize: "clamp(1.75rem, 3.5vw, 2.5rem)", fontWeight: isAr ? 800 : 500, color: "var(--heading)" }}>
              {isAr ? "نُجيب عن أكثر أسئلة الزوار شيوعاً" : "Answers to common visitor questions"}
            </h2>
          </div>
          <div className="space-y-4">
            {(isAr ? HOME_FAQS_AR : HOME_FAQS_EN).map((f) => (
              <details key={f.q} className="luxe-card p-5 group">
                <summary className={`${isAr ? "font-arabic" : ""} cursor-pointer font-medium list-none flex items-start justify-between gap-4`}
                         style={{ color: "var(--heading)" }}>
                  <span>{f.q}</span>
                  <span className="text-xl shrink-0 transition-transform group-open:rotate-45" style={{ color: "var(--gold-deep)" }}>+</span>
                </summary>
                <p className={`${isAr ? "font-arabic" : ""} mt-3 text-sm leading-relaxed`} style={{ color: "var(--ink-soft)" }}>{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA ----------------------------------------------------- */}
      <section className="section-y relative overflow-hidden" style={{ background: "var(--navy-deep)", color: "#fff" }}>
        <div className="absolute inset-0">
          <div className="absolute -top-32 -end-32 w-[420px] h-[420px] rounded-full blur-3xl opacity-20" style={{ background: "var(--gold)" }} />
          <div className="absolute -bottom-32 -start-32 w-[420px] h-[420px] rounded-full blur-3xl opacity-20" style={{ background: "var(--gold)" }} />
        </div>
        <div className="container-luxe relative text-center max-w-3xl">
          <p className="eyebrow" style={{ color: "var(--gold-soft)" }}>{isAr ? "ابدأ معنا" : "Begin"}</p>
          <h2 className={`mt-4 ${isAr ? "font-arabic" : "font-display"}`}
              style={{ fontSize: "clamp(2rem, 4vw, 3rem)", fontWeight: isAr ? 800 : 500 }}>
            {isAr ? "موقفك القانوني يستحق توضيحاً اليوم" : "Your legal position deserves clarity today."}
          </h2>
          <p className="mt-6 text-lg leading-relaxed" style={{ color: "rgba(245,241,232,0.78)" }}>
            {isAr
              ? "احجز استشارة 30 دقيقة، أو اسأل الذكاء القانوني الآن. أيهما أردت، نحن هنا."
              : "Book a 30-minute consultation, or ask the AI assistant now. Whichever you prefer, we are here."}
          </p>
          <div className="mt-10 flex flex-wrap gap-3 justify-center">
            <a href={waHome} target="_blank" rel="noopener noreferrer">
              <Button className="btn-gold rounded-sm px-8 h-12">{isAr ? "احجز استشارة عبر واتساب" : "Book via WhatsApp"}</Button>
            </a>
            <a href={waHome} target="_blank" rel="noopener noreferrer" data-testid="home-final-whatsapp">
              <Button className="btn-whatsapp rounded-sm px-8 h-12">
                <MessageCircle className="w-4 h-4 me-2" />
                {isAr ? "تواصل واتساب" : "WhatsApp Us"}
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
